import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {
    GetAllTickets,
    GetTicketByID,
    GetTicketsByEmployeeID,
    GetMyCreatedTickets,
    GetAssignedTickets,
    CreateTicket,
    UpdateTicket,
    DeleteTicket,
    AddTicketComment,
    DeleteTicketComment,
    GetTicketStats,
    EscalateTicket,
    UploadTicketAttachment,
    DownloadTicketAttachment,
    DeleteTicketAttachment,
    DownloadCommentAttachment,
    DeleteCommentAttachment
} from "helpers/api_method";

export const getAllTickets = createAsyncThunk("tickets/getAllTickets", async () => {
    try {
        const response = await GetAllTickets();
        return response.data;
    } catch (error) {
        return error;
    }
});

export const getTicketByID = createAsyncThunk("tickets/getTicketByID", async (ticketId: string) => {
    try {
        const response = await GetTicketByID(ticketId);
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        return error;
    }
});

export const getTicketsByEmployeeID = createAsyncThunk("tickets/getTicketsByEmployeeID", async (employeeId: string) => {
    try {
        const response = await GetTicketsByEmployeeID(employeeId);
        return response.data;
    } catch (error) {
        return error;
    }
});

export const getMyCreatedTickets = createAsyncThunk("tickets/getMyCreatedTickets", async () => {
    try {
        const response = await GetMyCreatedTickets();
        return response.data;
    } catch (error) {
        return error;
    }
});

export const getAssignedTickets = createAsyncThunk("tickets/getAssignedTickets", async () => {
    try {
        const response = await GetAssignedTickets();
        return response.data;
    } catch (error) {
        return error;
    }
});

export const createTicket = createAsyncThunk("tickets/createTicket", async (ticketData: any) => {
    try {
        const response = await CreateTicket(ticketData);
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        return error;
    }
});

export const updateTicket = createAsyncThunk("tickets/updateTicket", async ({ ticketId, ticketData }: { ticketId: string; ticketData: any }) => {
    try {
        const response = await UpdateTicket(ticketId, ticketData);
        toast.success("Ticket updated successfully", { autoClose: 2000 });
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        toast.error("Failed to update ticket", { autoClose: 2000 });
        return error;
    }
});

export const deleteTicket = createAsyncThunk("tickets/deleteTicket", async (ticketId: string) => {
    try {
        const response = await DeleteTicket(ticketId);
        toast.success("Ticket deleted successfully", { autoClose: 2000 });
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        toast.error("Failed to delete ticket", { autoClose: 2000 });
        return error;
    }
});

export const addTicketComment = createAsyncThunk("tickets/addTicketComment", async ({ ticketId, commentData }: { ticketId: string; commentData: any }) => {
    try {
        const response = await AddTicketComment(ticketId, commentData);
        toast.success("Comment added successfully", { autoClose: 2000 });
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        toast.error("Failed to add comment", { autoClose: 2000 });
        return error;
    }
});

export const deleteTicketComment = createAsyncThunk("tickets/deleteTicketComment", async ({ ticketId, commentId }: { ticketId: string; commentId: string }) => {
    try {
        const response = await DeleteTicketComment(ticketId, commentId);
        toast.success("Comment deleted successfully", { autoClose: 2000 });
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        toast.error("Failed to delete comment", { autoClose: 2000 });
        return error;
    }
});

export const getTicketStats = createAsyncThunk("tickets/getTicketStats", async () => {
    try {
        const response = await GetTicketStats();
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        return error;
    }
});

export const escalateTicket = createAsyncThunk("tickets/escalateTicket", async ({ ticketId, escalatedTo }: { ticketId: string; escalatedTo: string }) => {
    try {
        const response = await EscalateTicket(ticketId, { escalated_to: escalatedTo });
        toast.success("Ticket escalated successfully", { autoClose: 2000 });
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        toast.error("Failed to escalate ticket", { autoClose: 2000 });
        return error;
    }
});

export const uploadTicketAttachment = createAsyncThunk("tickets/uploadTicketAttachment", async ({ ticketId, formData }: { ticketId: string; formData: FormData }) => {
    try {
        const response = await UploadTicketAttachment(ticketId, formData);
        toast.success("Attachment uploaded successfully", { autoClose: 2000 });
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        toast.error("Failed to upload attachment", { autoClose: 2000 });
        return error;
    }
});

export const downloadTicketAttachment = createAsyncThunk("tickets/downloadTicketAttachment", async ({ ticketId, attachmentId }: { ticketId: string; attachmentId: string }) => {
    try {
        const response = await DownloadTicketAttachment(ticketId, attachmentId);
        return response;
    } catch (error) {
        toast.error("Failed to download attachment", { autoClose: 2000 });
        return error;
    }
});

export const deleteTicketAttachment = createAsyncThunk("tickets/deleteTicketAttachment", async ({ ticketId, attachmentId }: { ticketId: string; attachmentId: string }) => {
    try {
        const response = await DeleteTicketAttachment(ticketId, attachmentId);
        toast.success("Attachment deleted successfully", { autoClose: 2000 });
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        toast.error("Failed to delete attachment", { autoClose: 2000 });
        return error;
    }
});

export const downloadCommentAttachment = createAsyncThunk("tickets/downloadCommentAttachment", async ({ ticketId, commentId, attachmentId }: { ticketId: string; commentId: string; attachmentId: string }) => {
    try {
        const response = await DownloadCommentAttachment(ticketId, commentId, attachmentId);
        return response;
    } catch (error) {
        toast.error("Failed to download comment attachment", { autoClose: 2000 });
        return error;
    }
});

export const deleteCommentAttachment = createAsyncThunk("tickets/deleteCommentAttachment", async ({ ticketId, commentId, attachmentId }: { ticketId: string; commentId: string; attachmentId: string }) => {
    try {
        const response = await DeleteCommentAttachment(ticketId, commentId, attachmentId);
        toast.success("Comment attachment deleted successfully", { autoClose: 2000 });
        // Handle the API response structure: { data: {...}, message: "...", status: true }
        return response.data?.data || response.data;
    } catch (error) {
        toast.error("Failed to delete comment attachment", { autoClose: 2000 });
        return error;
    }
});
